/* eslint-disable no-param-reassign */
import { SERVICE_UUIDS } from './constants.js';
import { TOGETHER_FREE_GROUP_COUNT_ATTRIBUTE_TYPE } from './models/attributes/TogetherFreeGroupCount.js';
import { AMOUNT_ATTRIBUTE_ID, AMOUNT_ATTRIBUTE_TYPE } from './models/attributes/AmountAttribute.js';
import { END_DATE_LIST_ATTRIBUTE_TYPE } from './models/attributes/EndDateListAttribute.js';

/**
 * A class that contains logic related to additional post-processing of the models after they were parsed
 * from the OST response.
 */
export default class PostProcessor {
  /**
   * PostProcess Basket with custom logic.
   * Basket is mutated.
   *
   * @param {Basket} basket
   * @return {void}
   * @static
   */
  static postProcessBasket(basket) {
    PostProcessor._postProcessTogetherFree(basket);
    PostProcessor._postProcessTopUp(basket);
    PostProcessor._postProcessDataSharing(basket);
    PostProcessor._postProcessPrimaKids(basket);
    PostProcessor._postProcessButtons(basket);
    PostProcessor._postProcessPrima(basket);
  }

  /**
   * PostProcess togetherFree (TF) services in basket.
   * Services are mutated.
   *
   * @param {Basket} basket
   * @return {void}
   * @private
   * @static
   */
  static _postProcessTogetherFree(basket) {
    PostProcessor._postProcessTogetherFreeHideServices(basket);
    PostProcessor._postProcessTogetherFreeTwoGroupMember(basket);
    PostProcessor._postProcessTogetherFreeServicesCanBeDeactivated(basket);
  }

  /**
   * All TF services are set as not visible.
   * Services are mutated.
   *
   * @param {Basket} basket
   * @returns {void}
   * @private
   * @static
   */
  static _postProcessTogetherFreeHideServices(basket) {
    const { HIGHER_TARIFF, LOWER_TARIFF_PAID, SLAVE_MEMBER, GROUP_EXTEND, TWO_GROUP_MEMBER } =
      SERVICE_UUIDS.TOGETHER_FREE;

    // Services that are always hidden
    basket
      .getFilteredServices(service => [GROUP_EXTEND].includes(service.uuid))
      .forEach(service => {
        service.isVisible = false;
        service.isVisibleInSummary = false;
      });

    // Services that appear on the summary listing
    basket
      .getFilteredServices(service =>
        [HIGHER_TARIFF, LOWER_TARIFF_PAID, TWO_GROUP_MEMBER].includes(service.uuid),
      )
      .forEach(service => {
        service.isVisible = false;
        service.isVisibleInSummary = true;
      });

    // Services that appear on summary listing conditionally
    basket
      .getFilteredServices(service => [SLAVE_MEMBER].includes(service.uuid))
      .forEach(service => {
        service.isVisible = false;
        service.isVisibleInSummary = !basket.getServiceByUuid(TWO_GROUP_MEMBER)?.isActive;
      });
  }

  /**
   * Two group member service is set as `doNotDeactivate`
   * in case customer is in more than 1 slave group.
   * Services are mutated.
   *
   * @param {Basket} basket
   * @returns {void}
   * @private
   * @static
   */
  static _postProcessTogetherFreeTwoGroupMember(basket) {
    const tfServiceIds = SERVICE_UUIDS.TOGETHER_FREE;
    const twoGroupMember = basket.getServiceByUuid(tfServiceIds.TWO_GROUP_MEMBER);
    const slaveMember = basket.getServiceByUuid(tfServiceIds.SLAVE_MEMBER);
    if (!twoGroupMember || !slaveMember) {
      return;
    }

    const groupCountAttribute = slaveMember.getAttributeByType(
      TOGETHER_FREE_GROUP_COUNT_ATTRIBUTE_TYPE,
    );
    if (groupCountAttribute && groupCountAttribute.value > 1) {
      twoGroupMember.canBeDeactivated = false;
    }
  }

  /**
   * Sets both free and paid TogetherFree services as doNotDeactivate.
   * Services are mutated.
   *
   * @param {Basket} basket
   * @returns {void}
   * @private
   * @static
   */
  static _postProcessTogetherFreeServicesCanBeDeactivated(basket) {
    const { HIGHER_TARIFF, LOWER_TARIFF_PAID } = SERVICE_UUIDS.TOGETHER_FREE;

    [HIGHER_TARIFF, LOWER_TARIFF_PAID].forEach(uuid => {
      const service = basket.getServiceByUuid(uuid);
      if (service) {
        service.canBeDeactivated = false;
      }
    });
  }

  /**
   * PostProcess daily and weekly topup services in basket.
   * Services are mutated.
   *
   * For daily and weekly data pack is AmountAttribute set as hidden.
   *
   * If `detail` service is active, `EndDateListAttribute` is linked
   * from `detail` into `activation` service.
   *
   * @param {Basket} basket
   * @return {void}
   * @private
   * @static
   */
  static _postProcessTopUp(basket) {
    const { DAILY_DATA_PACK, WEEKLY_DATA_PACK } = SERVICE_UUIDS;

    basket
      .getFilteredServices(service =>
        [DAILY_DATA_PACK.DETAIL, WEEKLY_DATA_PACK.DETAIL].includes(service.uuid),
      )
      .forEach(service => {
        const amountAttribute = service.attributes.find(
          attr => attr.type === AMOUNT_ATTRIBUTE_TYPE,
        );
        if (amountAttribute !== undefined) {
          amountAttribute.isHidden = true;
        }

        if (service.isActive || service.isActivating || service.isDeactivating) {
          const activationServiceUuid =
            service.uuid === DAILY_DATA_PACK.DETAIL
              ? DAILY_DATA_PACK.ACTIVATION
              : WEEKLY_DATA_PACK.ACTIVATION;

          const activationService = basket.getServiceByUuid(activationServiceUuid);
          if (activationService) {
            const endDateListAttribute = service.attributes.find(
              attribute => attribute.type === END_DATE_LIST_ATTRIBUTE_TYPE,
            );
            if (endDateListAttribute) {
              activationService.attributes.push(endDateListAttribute);
            }
          }
        }
      });
  }

  /**
   * PostProcess dataSharing service.
   * Hide topup service from catalog.
   *
   * @param {Basket} basket
   * @static
   */
  static _postProcessDataSharing(basket) {
    const { DATA_SHARING_TOP_UP } = SERVICE_UUIDS;

    const service = basket.getServiceByUuid(DATA_SHARING_TOP_UP);
    if (service) {
      service.isVisible = false;
      service.isVisibleInSummary = false;
    }
  }

  /**
   * PostProcess Prima Kids services in basket.
   * Services are mutated.
   *
   * - Set main Prima Kids service as doNotDeactivate.
   * - Hide all services from Prima Kids catalog, including the profile service.
   *
   * @param {Basket} basket
   * @returns {void}
   * @private
   * @static
   */
  static _postProcessPrimaKids(basket) {
    const { MASTER_SERVICE } = SERVICE_UUIDS.PRIMA_KIDS;

    const masterService = basket.getServiceByUuid(MASTER_SERVICE);
    if (masterService) {
      masterService.canBeDeactivated = false;
    }

    const { PROFILE_JUNIOR } = SERVICE_UUIDS.PRIMA_KIDS;
    const profileService = basket.getServiceByUuid(PROFILE_JUNIOR);
    if (profileService) {
      profileService.isVisible = false;
      profileService.isVisibleInSummary = true;
    }
  }

  /**
   * Sets button flags for services.
   * Services are mutated.
   *
   * Activate button is hidden for:
   *  - TogetherFree Lower Tariff (paid)
   *  - Prima Kids
   *
   * TogetherFree TwoGroupMember service is deactivate button showed,
   * even if the service cannot be deactivated.
   *
   * For services that activates different serviceId by clicking on activate button,
   * is activate button showed even if they are active and deactivate button is never
   * showed.
   *
   * For Together free Two group member service is deactivate button showed even
   * when the service is set as doNotDeactivate.
   *
   * @param {Basket} basket
   * @return {void}
   * @private
   * @static
   */
  static _postProcessButtons(basket) {
    const { isSuspended, isChangingTariff } = basket;
    basket.services.forEach(service => {
      const isSpecialCaseService = [
        SERVICE_UUIDS.TOGETHER_FREE.LOWER_TARIFF_PAID,
        SERVICE_UUIDS.PRIMA_KIDS.MASTER_SERVICE,
      ].includes(service.uuid);

      const isServiceActivatingDifferentService = [
        SERVICE_UUIDS.DAILY_DATA_PACK.DETAIL,
        SERVICE_UUIDS.WEEKLY_DATA_PACK.DETAIL,
      ].includes(service.uuid);

      const isTogetherFreeTwoGroupMemberService =
        service.uuid === SERVICE_UUIDS.TOGETHER_FREE.TWO_GROUP_MEMBER;

      service.hasActivateButton =
        (!isSuspended &&
          (!isChangingTariff || service.canBeModifiedDuringTariffChange) &&
          !service.isInProcessing &&
          !service.hasUnprocessedRequest &&
          !service.isActive &&
          !isSpecialCaseService) ||
        isServiceActivatingDifferentService;

      service.hasDeactivateButton =
        !isSuspended &&
        (!isChangingTariff || service.canBeModifiedDuringTariffChange) &&
        !service.isInProcessing &&
        !service.hasUnprocessedRequest &&
        service.isActive &&
        (service.canBeDeactivated || isTogetherFreeTwoGroupMemberService) &&
        !isServiceActivatingDifferentService;
    });
  }

  /**
   * Hides Prima migration services from being displayed in the service list.
   * Mutates Basket object.
   *
   * @param {Basket} basket
   * @private
   */
  static _postProcessPrima(basket) {
    basket.services.forEach(service => {
      const { PRIMA_DATA, PRIMA_GO, PRIMA_SAVER } = SERVICE_UUIDS.PRIMA_MIGRATION_SERVICE;
      if (
        service.uuid === PRIMA_DATA ||
        service.uuid === PRIMA_GO ||
        service.uuid === PRIMA_SAVER
      ) {
        service.isVisible = false;
        service.isVisibleInSummary = false;
      }
    });
  }

  /**
   * PostProcess Summary with custom logic.
   *
   * Mutates Summary object.
   *
   * @param {Summary} summary
   * @returns {void}
   * @static
   */
  static postProcessSummary(summary) {
    summary.addedItems.forEach(summaryItem =>
      PostProcessor._postProcessSummaryItem(summaryItem, 'added'),
    );
    summary.removedItems.forEach(summaryItem =>
      PostProcessor._postProcessSummaryItem(summaryItem, 'removed'),
    );
    summary.changedItems.forEach(summaryItem =>
      PostProcessor._postProcessSummaryItem(summaryItem, 'changed'),
    );
  }

  /**
   * Modifies SummaryItem by custom logic.
   * Mutates summaryItem object.
   *
   * @param {SummaryItem} summaryItem
   * @param {'added'|'removed'|'changed'} state
   * @private
   * @static
   */
  static _postProcessSummaryItem(summaryItem, state) {
    PostProcessor._postProcessSummaryItemAttributes(summaryItem, state);
    PostProcessor._postProcessSummaryItemPrimaKids(summaryItem);
  }

  /**
   * Modifies SummaryItem attributes.
   * Mutates summaryItem object.
   *
   * @param {SummaryItem} summaryItem
   * @param {'added'|'removed'|'changed'} state
   * @private
   * @static
   */
  static _postProcessSummaryItemAttributes(summaryItem, state) {
    summaryItem.attributes.forEach(attribute => {
      switch (attribute.id) {
        case AMOUNT_ATTRIBUTE_ID:
          PostProcessor._postProcessSummaryItemWithAmountAttribute(summaryItem, attribute, state);
          break;
        default:
          if (attribute.newValue === null) {
            attribute.newValue = '-';
          }
          break;
      }
    });
  }

  /**
   * PostProcess `summaryItem` with `Amount` attribute.
   * Mutates `summaryItem` object.
   *
   * - `SummaryItem` name is extended with Amount value in format `(Nx)`
   * - daily and weekly data pack name is not extended
   *
   * @param {SummaryItem} summaryItem
   * @param {{id: string, oldValue: string, newValue: string}} attribute
   * @param {'added'|'removed'|'changed'} state
   * @returns {void}
   * @private
   * @static
   */
  static _postProcessSummaryItemWithAmountAttribute(summaryItem, attribute, state) {
    const { DAILY_DATA_PACK, WEEKLY_DATA_PACK } = SERVICE_UUIDS;
    if ([DAILY_DATA_PACK.DETAIL, WEEKLY_DATA_PACK.DETAIL].includes(summaryItem.uuid)) {
      return;
    }

    if (state === 'removed' && attribute.oldValue !== null && attribute.oldValue > 1) {
      summaryItem.name += ` (${attribute.oldValue}x)`;
    } else if (attribute.newValue !== null && attribute.newValue > 1) {
      summaryItem.name += ` (${attribute.newValue}x)`;
    }
  }

  /**
   * Modifies summary Prima Kids items.
   * Mutates summaryItem object.
   *
   * - Profile service (Prima Kids Junior) is renamed to "Prima Kids".
   *
   * TODO: In case Summary will accept whole basket, this should be refactored
   * to use Prima Kids main service name instead of fixed string.
   *
   * @param {SummaryItem} summaryItem
   * @static
   * @private
   */
  static _postProcessSummaryItemPrimaKids(summaryItem) {
    if (summaryItem.uuid === SERVICE_UUIDS.PRIMA_KIDS.PROFILE_JUNIOR) {
      summaryItem.name = 'Prima Kids';
    }
  }
}
